import { useCallback, useEffect, useRef, useState } from 'react';

// CORE-1795: `?render=explicit` is REQUIRED. Without it, Cloudflare's api.js
// auto-scans the DOM on load for any `.cf-turnstile` element and renders it
// itself — including one whose `data-sitekey` we set to `''` because the env
// var was missing, which throws an uncaught `TurnstileError` from inside
// Cloudflare's own script before our code ever gets a chance to guard against
// it. `render=explicit` disables that auto-scan so the widget is ONLY ever
// created by our own explicit `window.turnstile.render()` call in
// `renderWidget`, which we only reach once we've already validated the site
// key ourselves.
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// error-callback allows 2 automatic retries (3 total attempts) before the
// widget is considered terminally blocked.
const MAX_ERROR_RETRIES = 2;

export type TTurnstileStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'
  | 'blocked'
  // CORE-1795: `NEXT_PUBLIC_TURNSTILE_SITE_KEY` is missing at build time — a
  // deploy/config problem, NOT a Cloudflare-side failure ('error') and NOT a
  // security-driven lockout ('blocked'). Kept distinct so consumers can show a
  // "misconfigured" message instead of a generic captcha error, and so this
  // case is never confused with the two failure modes that are intentionally
  // fail-closed.
  | 'unconfigured';

export type TUseTurnstileReturn = {
  token: string | undefined;
  status: TTurnstileStatus;
  reset: () => void;
};

type TTurnstileRenderOptions = {
  sitekey: string;
  size?: 'invisible' | 'normal' | 'compact';
  callback?: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
};

type TTurnstileGlobal = {
  render: (container: HTMLElement, options: TTurnstileRenderOptions) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TTurnstileGlobal;
  }
}

// Module-level singleton: the Turnstile script must be requested exactly
// once no matter how many `useTurnstile()` instances mount (e.g. React
// StrictMode double-invoke, or multiple forms on the same route).
let turnstileScriptPromise: Promise<void> | undefined;

const loadTurnstileScript = (): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.reject(
      new Error('Turnstile can only be loaded in the browser'),
    );
  }

  if (window.turnstile) {
    return Promise.resolve();
  }

  if (!turnstileScriptPromise) {
    turnstileScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        // Allow a future mount to retry the load instead of failing forever.
        turnstileScriptPromise = undefined;
        reject(new Error('Failed to load the Turnstile script'));
      };
      document.body.appendChild(script);
    });
  }

  return turnstileScriptPromise;
};

/**
 * Loads Cloudflare Turnstile (invisible mode) and exposes the resulting
 * token to the caller. Mirrors the shape of `useRequestRecovery`: a single
 * hook the consuming form gates its submit button on.
 *
 * - The Turnstile script loads once (module-level singleton), idempotent
 *   across mounts.
 * - The widget renders into a hidden container appended to `document.body`
 *   (invisible mode has no visible UI, so it doesn't need to live in the
 *   form's DOM tree).
 * - `error-callback` retries automatically up to `MAX_ERROR_RETRIES` times
 *   before moving to the terminal `'blocked'` status.
 * - `expired-callback` (~5 min token lifetime) transparently resets the
 *   widget to fetch a fresh token, without counting against the retry budget.
 * - `reset()` lets the consumer manually recover from `'blocked'` (e.g. a
 *   "try again" affordance) — it clears the retry budget too.
 * - CORE-1795: if `NEXT_PUBLIC_TURNSTILE_SITE_KEY` is missing, NO container is
 *   ever appended to the DOM and the script isn't even requested — status
 *   goes straight to `'unconfigured'`. This is a build/deploy configuration
 *   problem (the var is inlined at Next.js BUILD time), not a runtime
 *   Cloudflare failure.
 */
export const useTurnstile = (): TUseTurnstileReturn => {
  const [token, setToken] = useState<string>();
  const [status, setStatus] = useState<TTurnstileStatus>('idle');

  const containerRef = useRef<HTMLDivElement>();
  const widgetIdRef = useRef<string | undefined>(undefined);
  const retryCountRef = useRef(0);
  const isMountedRef = useRef(true);

  const renderWidget = useCallback(() => {
    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

    if (!window.turnstile || !siteKey || !containerRef.current) {
      setStatus('error');
      return;
    }

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      size: 'invisible',
      callback: newToken => {
        if (!isMountedRef.current) {
          return;
        }
        setToken(newToken);
        setStatus('ready');
      },
      'error-callback': () => {
        if (!isMountedRef.current) {
          return;
        }

        if (retryCountRef.current < MAX_ERROR_RETRIES) {
          retryCountRef.current += 1;
          setStatus('loading');
          window.turnstile?.reset(widgetIdRef.current);
          return;
        }

        setStatus('blocked');
      },
      'expired-callback': () => {
        if (!isMountedRef.current) {
          return;
        }
        // Not a retry — the widget just needs a fresh token.
        setToken(undefined);
        setStatus('loading');
        window.turnstile?.reset(widgetIdRef.current);
      },
    });
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

    // CORE-1795: guard BEFORE touching the DOM. Appending a `.cf-turnstile`
    // container with `data-sitekey=""` let Cloudflare's own script auto-render
    // it and throw an uncaught `TurnstileError` — this check must run first so
    // that never happens, not just be "eventually caught" by renderWidget's
    // own guard (which runs too late, after the container already exists).
    if (!siteKey) {
      // Intentional console.error (noConsole is off in this repo's biome config): an
      // actionable diagnostic for the next developer/on-call, not a linted anti-pattern.
      console.error(
        '[CORE-1795] NEXT_PUBLIC_TURNSTILE_SITE_KEY is not set — Turnstile cannot render. ' +
          'This var is inlined at BUILD time in Next.js: it must be present in the build ' +
          'environment and the app must be rebuilt.',
      );
      setStatus('unconfigured');
      return () => {
        isMountedRef.current = false;
      };
    }

    setStatus('loading');

    const container = document.createElement('div');
    container.className = 'cf-turnstile';
    container.setAttribute('data-sitekey', siteKey);
    container.setAttribute('data-size', 'invisible');
    container.style.display = 'none';
    document.body.appendChild(container);
    containerRef.current = container;

    loadTurnstileScript()
      .then(() => {
        if (!isMountedRef.current) {
          return;
        }
        renderWidget();
      })
      .catch(() => {
        if (!isMountedRef.current) {
          return;
        }
        setStatus('error');
      });

    return () => {
      isMountedRef.current = false;

      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }

      container.remove();
      containerRef.current = undefined;
    };
  }, [renderWidget]);

  const reset = useCallback(() => {
    retryCountRef.current = 0;
    setToken(undefined);
    setStatus('loading');

    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    } else {
      renderWidget();
    }
  }, [renderWidget]);

  return { token, status, reset };
};
